/**
 * useAudioRecording Hook
 * Custom hook for managing audio recording and screening questions
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  getUserAudioResponses,
  getScreeningQuestions,
  getUserScreeningProfile,
  uploadAudioRecording,
  updateScreeningProfile,
  deleteAudioRecording,
  getAudioResponseByQuestion
} from '../services/audioService';
import { useAuth } from '../../../core/contexts/AuthContext';
import { toast } from 'react-hot-toast';

/**
 * Custom hook for audio recording management
 * 
 * @returns {Object} Audio recording data and management functions
 */
const useAudioRecording = () => {
  const { currentUser } = useAuth();
  
  // State management
  const [audioResponses, setAudioResponses] = useState([]);
  const [screeningQuestions, setScreeningQuestions] = useState([]);
  const [screeningProfile, setScreeningProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  // Load screening questions and user responses
  const loadData = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const [questions, responses, profile] = await Promise.all([
        getScreeningQuestions(),
        getUserAudioResponses(currentUser.uid),
        getUserScreeningProfile(currentUser.uid)
      ]);
      
      setScreeningQuestions(questions);
      setAudioResponses(responses);
      setScreeningProfile(profile);
    } catch (err) {
      console.error('Error loading audio data:', err);
      setError(err.message || 'Failed to load screening data');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Upload audio response
  const handleUploadAudio = useCallback(async (audioBlob, questionId, duration) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    
    try {
      setUploading(true);
      setError(null);
      
      const uploadedAudio = await uploadAudioRecording(
        audioBlob, 
        currentUser.uid, 
        questionId, 
        duration
      );
      
      // Add to responses list or update existing
      setAudioResponses(prev => {
        const existingIndex = prev.findIndex(response => 
          response.questionId === questionId
        );
        
        if (existingIndex >= 0) {
          // Update existing response
          const newResponses = [...prev];
          newResponses[existingIndex] = uploadedAudio;
          return newResponses;
        } else {
          // Add new response
          return [uploadedAudio, ...prev];
        }
      });
      
      toast.success('Audio response uploaded successfully!');
      return uploadedAudio;
    } catch (err) {
      console.error('Error uploading audio:', err);
      setError(err.message || 'Failed to upload audio');
      toast.error(err.message || 'Failed to upload audio');
      throw err;
    } finally {
      setUploading(false);
    }
  }, [currentUser]);

  // Delete audio response
  const handleDeleteAudio = useCallback(async (audioId) => {
    if (!currentUser) return;
    
    try {
      setError(null);
      
      await deleteAudioRecording(audioId);
      
      // Remove from responses list
      setAudioResponses(prev => prev.filter(response => response.id !== audioId));
      
      toast.success('Audio response deleted');
    } catch (err) {
      console.error('Error deleting audio:', err);
      setError(err.message || 'Failed to delete audio');
      toast.error(err.message || 'Failed to delete audio');
      throw err;
    }
  }, [currentUser]);

  // Complete screening profile
  const handleCompleteScreening = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      setError(null);
      
      const audioIds = audioResponses.map(response => response.id);
      await updateScreeningProfile(currentUser.uid, audioIds);
      
      // Update screening profile state
      const updatedProfile = {
        userId: currentUser.uid,
        audioResponses: audioIds,
        completedAt: new Date(),
        status: 'completed'
      };
      
      setScreeningProfile(updatedProfile);
      
      toast.success('Screening profile completed!');
      return updatedProfile;
    } catch (err) {
      console.error('Error completing screening:', err);
      setError(err.message || 'Failed to complete screening');
      toast.error(err.message || 'Failed to complete screening');
      throw err;
    }
  }, [currentUser, audioResponses]);

  // Get response for specific question
  const getResponseForQuestion = useCallback((questionId) => {
    return audioResponses.find(response => response.questionId === questionId);
  }, [audioResponses]);

  // Check if question has response
  const hasResponseForQuestion = useCallback((questionId) => {
    return audioResponses.some(response => response.questionId === questionId);
  }, [audioResponses]);

  // Get completion progress
  const getProgress = useCallback(() => {
    if (screeningQuestions.length === 0) return 0;
    
    const answeredQuestions = screeningQuestions.filter(question => 
      hasResponseForQuestion(question.id)
    ).length;
    
    return Math.round((answeredQuestions / screeningQuestions.length) * 100);
  }, [screeningQuestions, hasResponseForQuestion]);

  // Check if screening is complete
  const isScreeningComplete = useCallback(() => {
    if (screeningQuestions.length === 0) return false;
    
    return screeningQuestions.every(question => 
      hasResponseForQuestion(question.id)
    );
  }, [screeningQuestions, hasResponseForQuestion]);

  // Get statistics
  const getStats = useCallback(() => {
    const totalDuration = audioResponses.reduce((sum, response) => 
      sum + (response.duration || 0), 0
    );
    
    return {
      totalResponses: audioResponses.length,
      totalQuestions: screeningQuestions.length,
      completionRate: getProgress(),
      totalDuration: Math.round(totalDuration),
      averageDuration: audioResponses.length > 0 ? 
        Math.round(totalDuration / audioResponses.length) : 0,
      isComplete: isScreeningComplete(),
      hasProfile: !!screeningProfile
    };
  }, [audioResponses, screeningQuestions, getProgress, isScreeningComplete, screeningProfile]);

  // Refresh data
  const refresh = useCallback(() => {
    loadData();
  }, [loadData]);

  // Load data on mount and when user changes
  useEffect(() => {
    if (currentUser) {
      loadData();
    } else {
      // Clear state when user logs out
      setAudioResponses([]);
      setScreeningQuestions([]);
      setScreeningProfile(null);
      setLoading(false);
      setError(null);
    }
  }, [currentUser, loadData]);

  return {
    // Data
    audioResponses,
    screeningQuestions,
    screeningProfile,
    
    // State
    loading,
    uploading,
    error,
    
    // Actions
    uploadAudio: handleUploadAudio,
    deleteAudio: handleDeleteAudio,
    completeScreening: handleCompleteScreening,
    refresh,
    
    // Utilities
    getResponseForQuestion,
    hasResponseForQuestion,
    getProgress,
    isScreeningComplete,
    getStats
  };
};

export default useAudioRecording;